// 試合盤面 orchestrator (= DDSIM-20 / DDSIM-26、 ADR-0025 §B.3 / §A 機能優先)。
//
// landscape レイアウト: 盤面全体を viewport 1 画面に収める。
// 縦順 (top → bottom): 相手 StatLine → 相手 PlayerMat → 中央 HUD → 自分 PlayerMat → 自分 StatLine。
// 各 PlayerMat は内部で「バトル行 (field) + 手札行 (deck/次元 を手札と同列)」を持つ
// (mirrored=相手 は手札行が上 / field が中央側)。
//
// 操作: Main + 自手番で hand click → SUMMON
//       Battle + 自手番で自 field click → attacker 選択 → 相手 field click で攻撃
//       DIRECT ATTACK ボタンは attacker 選択中に表示。

import { useState } from "react";

import type { CardView } from "@/bindings/CardView";
import type { MatchSnapshot } from "@/bindings/MatchSnapshot";
import { Button } from "@/components/ui/button";
import { PhaseStrip, TurnHud } from "./Hud";
import { StatLine } from "./tiles";
import { PlayerMat } from "./PlayerMat";

interface BoardProps {
  snapshot: MatchSnapshot;
  onReady: () => void;
  onPhaseAdvance: () => void;
  onSummon: (card: CardView) => void;
  onAttack: (attacker: CardView, target: CardView | null) => void;
  onSetInitialCharge: (card: CardView) => void;
  onCharge: (card: CardView) => void;
  busy: boolean;
}

export function Board(props: BoardProps): JSX.Element {
  const {
    snapshot,
    onReady,
    onPhaseAdvance,
    onSummon,
    onAttack,
    onSetInitialCharge,
    onCharge,
    busy,
  } = props;
  const [selectedAttacker, setSelectedAttacker] = useState<CardView | null>(null);

  // viewer=P1 固定: players[0]=自分 (P1)、 players[1]=相手 (P2)。
  const me = snapshot.players[0];
  const opponent = snapshot.players[1];
  if (me === undefined || opponent === undefined) {
    return <p className="text-destructive">snapshot に player が揃っていません</p>;
  }

  const waitingForReady = snapshot.match_phase === "WaitingForReady";
  const inGame = snapshot.match_phase === "InGame";
  const isMyTurn = snapshot.current_player === snapshot.viewer;
  const canSummon = inGame && snapshot.current_phase === "Main" && isMyTurn;
  const canAttack = inGame && snapshot.current_phase === "Battle" && isMyTurn;
  // Charge phase の補充 (= 1 ターン 1 回、 ADR-0030 §C)。 has_charged_this_turn flag を見る。
  const canCharge =
    inGame &&
    snapshot.current_phase === "Charge" &&
    isMyTurn &&
    !me.has_charged_this_turn;
  // WaitingForReady 中の初期 charge 配置: 未完了で 2 枚未満のみ受付。
  const canSetInitialCharge =
    waitingForReady && !me.ready_confirmed && me.charge.count < 2;
  // READY ボタンは 2 枚配置完了 + 未 confirmed の時のみ active。
  const readyButtonDisabled = busy || me.ready_confirmed || me.charge.count < 2;

  // attacker 選択 toggle (同一カード再 click で解除)。
  function handleMyFieldClick(card: CardView): void {
    if (!canAttack) return;
    setSelectedAttacker((prev) =>
      prev?.instance_id === card.instance_id ? null : card,
    );
  }

  // 相手 field click → 攻撃実行。
  function handleOpponentFieldClick(card: CardView): void {
    if (!canAttack || selectedAttacker === null) return;
    onAttack(selectedAttacker, card);
    setSelectedAttacker(null);
  }

  // ダイレクトアタック。
  function handleDirectAttack(): void {
    if (selectedAttacker === null) return;
    onAttack(selectedAttacker, null);
    setSelectedAttacker(null);
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-0.5 rounded-lg border border-slate-700 bg-slate-900 p-2 text-slate-100">
      {/* 相手 StatLine */}
      <StatLine label="OPPONENT (P2)" player={opponent} />

      {/* 相手 mat (mirrored: 手札行 → バトル行、 field が中央側) */}
      <PlayerMat
        player={opponent}
        mirrored
        onFieldClick={handleOpponentFieldClick}
        fieldClickable={canAttack && selectedAttacker !== null}
        fieldDisabled={busy}
      />

      {/* 4. 中央 HUD バー (= フェーズ帯 + ターン/手番 + 操作ボタン) */}
      <section className="flex flex-col gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5">
        <PhaseStrip current={snapshot.current_phase} inGame={inGame} />
        <div className="flex items-center justify-between">
          <TurnHud
            turn={snapshot.turn_count}
            isMyTurn={isMyTurn}
            inGame={inGame}
          />
          <div className="flex items-center gap-2">
            {waitingForReady && (
              <span className="text-xs text-slate-400">
                初期 charge: {me.charge.count}/2
              </span>
            )}
            {waitingForReady && (
              <Button size="sm" onClick={onReady} disabled={readyButtonDisabled}>
                READY (P1)
              </Button>
            )}
            {canAttack && selectedAttacker !== null && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDirectAttack}
                disabled={busy}
              >
                DIRECT ATTACK
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={onPhaseAdvance}
              disabled={busy || !inGame}
            >
              PHASE ADVANCE
            </Button>
          </div>
        </div>
      </section>

      {/* 自分 mat (バトル行 → 手札行、 field が中央側) */}
      <PlayerMat
        player={me}
        mirrored={false}
        onFieldClick={handleMyFieldClick}
        fieldClickable={canAttack}
        fieldSelectedId={selectedAttacker?.instance_id ?? null}
        fieldDisabled={busy}
        canSummon={canSummon}
        onSummon={onSummon}
        canSetInitialCharge={canSetInitialCharge}
        onSetInitialCharge={onSetInitialCharge}
        canCharge={canCharge}
        onCharge={onCharge}
        busy={busy}
      />

      {/* 自分 StatLine */}
      <StatLine label="YOU (P1)" player={me} />
    </div>
  );
}
